import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { supabase } from '../../lib/supabase';

const HiringPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    socialLink: '',
    website: '',
    demoReelUrl: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { data, error: submitError } = await supabase?.from('hiring_applications')?.insert([
          {
            full_name: formData?.fullName,
            email: formData?.email,
            social_link: formData?.socialLink,
            website: formData?.website,
            demo_reel_url: formData?.demoReelUrl,
            message: formData?.message
          }
        ])?.select();

      if (submitError) throw submitError;

      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        socialLink: '',
        website: '',
        demoReelUrl: '',
        message: ''
      });

      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      console.error('Error submitting application:', err);
      setError(err?.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Join Our Team - RoleCall Mentor Opportunities</title>
        <meta name="description" content="Join RoleCall as a professional theatre mentor. We're hiring experienced musical theatre actors in Los Angeles and Orange County." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        {/* Navigation */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-10 w-auto"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Back to Home
          </Button>
        </nav>

        {/* Hero Section */}
        <section className="container mx-auto px-6 py-12">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h1 className="text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                Join Our Team
              </h1>
              <p className="text-2xl text-blue-100 mb-4">
                We're Hiring Professional Theatre Actors
              </p>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                Currently hiring in Los Angeles and Orange County only
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              {/* About Section */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-16 h-16 rounded-xl flex items-center justify-center mr-4">
                    <Icon name="Users" size={32} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold text-white">
                    Supporting the Theatre Community
                  </h2>
                </div>
                <p className="text-blue-100 mb-4 leading-relaxed">
                  At RoleCall, we believe in supporting professional theatre actors and giving back to the community that has given us so much. 
                  We're building a platform that not only helps aspiring performers but also provides opportunities for experienced professionals 
                  to share their knowledge and earn income doing what they love.
                </p>
                <p className="text-blue-100 leading-relaxed">
                  As a RoleCall mentor, you'll create instructional content, provide coaching insights, and help shape the next generation 
                  of musical theatre performers while maintaining your performance career.
                </p>
              </motion.div>

              {/* Benefits Section */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8"
              >
                <h2 className="text-3xl font-heading font-bold text-white mb-6">
                  What We Offer
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Icon name="Check" size={24} className="text-green-400 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-white font-semibold mb-1">Flexible Schedule</div>
                      <div className="text-blue-100">Create content on your own time, work around your performance schedule</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={24} className="text-green-400 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-white font-semibold mb-1">Competitive Compensation</div>
                      <div className="text-blue-100">Earn income sharing your expertise with aspiring performers</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={24} className="text-green-400 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-white font-semibold mb-1">Professional Development</div>
                      <div className="text-blue-100">Enhance your teaching skills and expand your professional network</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={24} className="text-green-400 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-white font-semibold mb-1">Technical Support</div>
                      <div className="text-blue-100">We provide equipment, studio space, and production assistance</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={24} className="text-green-400 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-white font-semibold mb-1">Community Impact</div>
                      <div className="text-blue-100">Make a lasting difference in students' lives and careers</div>
                    </div>
                  </li>
                </ul>
              </motion.div>
            </div>

            {/* Requirements Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6">
                What We're Looking For
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="space-y-3">
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Professional theatre experience (Broadway, national tours, regional theatre)
                  </li>
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Strong vocal technique and performance skills
                  </li>
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Located in Los Angeles or Orange County
                  </li>
                </ul>
                <ul className="space-y-3">
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Passion for teaching and mentoring
                  </li>
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Comfortable on camera and creating video content
                  </li>
                  <li className="flex items-center text-blue-100">
                    <Icon name="Star" size={20} className="text-yellow-400 mr-3 flex-shrink-0" />
                    Available for in-person content creation sessions
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Application Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8"
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6 text-center">
                Apply to Join Our Team
              </h2>

              {submitSuccess ? (
                <div className="text-center py-12">
                  <div className="bg-green-500 bg-opacity-20 border border-green-400 rounded-2xl p-8 mb-6">
                    <Icon name="CheckCircle" size={64} className="text-green-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold text-white mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-blue-100">
                      Thank you for your interest in joining RoleCall. We'll review your application and get back to you soon.
                    </p>
                  </div>
                  <p className="text-blue-200">
                    Redirecting to homepage...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Full Name"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                      className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200"
                    />
                    <Input
                      label="Email"
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                      className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Social Media Link"
                      name="socialLink"
                      value={formData?.socialLink}
                      onChange={handleInputChange}
                      placeholder="Instagram, TikTok, or LinkedIn profile"
                      className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200"
                    />
                    <Input
                      label="Website"
                      name="website"
                      value={formData?.website}
                      onChange={handleInputChange}
                      placeholder="Your personal website or portfolio"
                      className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200"
                    />
                  </div>

                  <Input
                    label="Demo Reel"
                    name="demoReelUrl"
                    value={formData?.demoReelUrl}
                    onChange={handleInputChange}
                    required
                    placeholder="YouTube, Vimeo, or other video link showcasing your performance"
                    className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200"
                  />

                  <div>
                    <label className="block text-white font-medium mb-2">
                      Tell Us About Yourself
                    </label>
                    <textarea
                      name="message"
                      value={formData?.message}
                      onChange={handleInputChange}
                      rows={6}
                      placeholder="Share your experience, notable roles, training background, and why you'd like to become a RoleCall mentor..."
                      className="w-full px-4 py-3 rounded-xl bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500 bg-opacity-20 border border-red-400 rounded-xl p-4">
                      <p className="text-red-200 flex items-center">
                        <Icon name="AlertCircle" size={20} className="mr-2" />
                        {error}
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="large"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl"
                    iconName={isSubmitting ? null : "Send"}
                    iconPosition="left"
                  >
                    {isSubmitting ? (
                      <LoadingSpinner size="small" text="Submitting..." />
                    ) : (
                      'Submit Application'
                    )}
                  </Button>

                  <p className="text-blue-200 text-sm text-center">
                    By submitting this form, you agree to be contacted by RoleCall regarding mentor opportunities.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-black bg-opacity-30 mt-20">
          <div className="container mx-auto px-6 text-center">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-8 w-auto mx-auto mb-4"
            />
            <p className="text-blue-200 mb-4">
              Supporting the theatre community, one performance at a time
            </p>
            <div className="flex items-center justify-center space-x-6 text-blue-200">
              <a href="/privacy-policy-page" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms-of-service-page" className="hover:text-white transition-colors">
                Terms of Service
              </a>
              <a href="/support-page" className="hover:text-white transition-colors">
                Support
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HiringPage;